import React, { useEffect, useState } from "react";
import FileGrid from "./file-grid";
import FileDetails from "./file-details";
import MediaTabs from "./media-tabs";
import Pagination from "../../../../../components/shared/pagination";
import { getAllVehicleMedia } from "../../../../../services/vehicles-service";
import Loader from "../../../../../components/shared/loader";
import { IoSearchOutline } from "react-icons/io5";
import DateRangePicker from "../../../../../components/shared/date-range-picker";

const Media = ({ accountId, vehicleId, media: uploadedMedia, loading: uploadLoading, error: uploadError, newlyAddedItem, globalFilter, onGlobalFilterChange, onRemoveImage, collapsed }) => {
  const [tab, setTab] = useState("all");
  const [selectedFile, setSelectedFile] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState(globalFilter || "");
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
    endDate: new Date(),
  });

  // Determine file type for filtering
  const getFileType = (file) => {
    if (file?.fileType?.startsWith("image/")) return "image";
    if (file?.fileType === "application/pdf") return "document";
    return "document";
  };

  // Fetch media from API
  const fetchMedia = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getAllVehicleMedia(accountId, vehicleId);

      if (res.success) {
        setMedia((prev) => {
          const localFiles = prev.filter((item) => item.isLocal);
          const apiFiles = (res.data.data || []).sort(
            (a, b) => new Date(b.createdOn) - new Date(a.createdOn)
          );
          const uniqueApiFiles = apiFiles.filter(
            (apiFile) => !localFiles.some((local) => local.mediaId === apiFile.mediaId)
          );
          return [...localFiles, ...uniqueApiFiles];
        });
        setCurrentPage(1);
      } else {
        setError(res.message || "Failed to fetch media");
      }
    } catch (error) {
      setError(error.message || "An error occurred while fetching media");
    } finally {
      setLoading(false);
    }
  };

  // Handle file removal
  const handleRemoveImage = (id) => {
    setMedia((prev) => prev.filter((item) => (item.mediaId || item.localId) !== id));
    if (onRemoveImage) {
      onRemoveImage(id);
    }
  };

  // Update media state when uploadedMedia changes
  useEffect(() => {
  if (!uploadedMedia) return;

  const newItems = Array.isArray(uploadedMedia) ? uploadedMedia : [uploadedMedia];

  setMedia((prev) => {
    const prevMap = new Map(prev.map((item) => [item.mediaId, item]));
    
    newItems.forEach((item) => {
      if (item.mediaId) {
        // Replace if exists, or add new
        prevMap.set(item.mediaId, item);
      }
    });

    // Return new array sorted by createdOn desc (optional)
    return Array.from(prevMap.values()).sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn));
  });
}, [uploadedMedia]);




  // Fetch media on mount or when accountId/vehicleId changes
  useEffect(() => {
    if (accountId && vehicleId) {
      fetchMedia();
    }
  }, [accountId, vehicleId]);

  // Handle search term changes
  useEffect(() => {
    setCurrentPage(1);
    if (onGlobalFilterChange) {
      onGlobalFilterChange(searchTerm);
    }
  }, [searchTerm, onGlobalFilterChange]);

  // Reset selected file and page when tab changes
  useEffect(() => {
    setSelectedFile(null);
    setCurrentPage(1);
  }, [tab]);

  console.log(media)

  // Filter media based on tab, search term, and date range
  const filteredMedia = media.filter((file) => {
    if (!file) return false;
    const matchesTab = tab === "all" || getFileType(file) === tab;
    const matchesSearch = file.fileName?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
    // const fileDate = file.createdOn ? new Date(file.createdOn) : new Date();
    // const matchesDate = fileDate >= dateRange.startDate && fileDate <= dateRange.endDate;
    return matchesTab && matchesSearch ;
    // && matchesDate;
  });

  // Calculate paginated data
  const totalItems = filteredMedia.length;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredMedia.slice(indexOfFirstItem, indexOfLastItem);

  // Combine errors
  const displayError = error || uploadError;

  // Show loader if either fetching or uploading
  if (loading) {
    return (
      <div className="flex flex-col w-full">
        <div className="w-full flex justify-center items-center h-full">
          <Loader />
        </div>
      </div>
    );
  }

  // Show error if present
  if (displayError) {
    return (
      <div className="flex flex-col w-full h-screen">
        <div className="w-full flex justify-center items-center min-h-56 h-full">
          <div className="text-red-600 bg-red-100 px-4 py-2 rounded-md">
            {displayError}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full min-h-[60vh]">
      <div className="flex -ml-2 gap-3 flex-wrap mt-2 mb-4">
        <div className="bg-white rounded-md px-3 flex border border-border items-center gap-2 w-44 lg:w-72 py-1">
          <IoSearchOutline className="text-gray-500 text-lg" />
          <input
            type="text"
            className="bg-transparent outline-none py-1 w-full text-sm text-gray-700"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <DateRangePicker onChange={setDateRange} />
      </div>

      <MediaTabs activeTab={tab} onTabChange={setTab} />

      {filteredMedia.length === 0 ? (
        <div className="w-full flex justify-center items-center min-h-56 h-full">
          <p className="text-gray-500">
            {searchTerm ? "No matching media files found" : "No media files found"}
          </p>
        </div>
      ) : (
        <>
          <div className="flex gap-6 items-start flex-grow overflow-hidden relative">
            <FileGrid
              files={currentItems}
              selectedFile={selectedFile}
              onFileSelect={setSelectedFile}
              newlyAddedItem={newlyAddedItem}
              collapsed={collapsed}
            />

            {/* Mobile (up to md) - Slide-in panel */}
            <div onClick={() => setSelectedFile(false)} className={`
    md:hidden absolute inset-y-0 right-0 z-50 w-full h-full bg-[#00000040] flex justify-end
    transition-transform duration-300 ease-in-out
    ${selectedFile ? 'translate-x-0' : 'translate-x-full'}
  `}>
              {selectedFile && (
                <FileDetails
                  file={selectedFile}
                  onClose={() => setSelectedFile(null)}
                  setMedia={setMedia}
                  accountId={accountId}
                  onRemoveImage={handleRemoveImage}
                  collapsed={collapsed}
                />
              )}
            </div>

            {/* Desktop (md and up) - Always visible */}
            <FileDetails
              file={selectedFile}
              onClose={() => setSelectedFile(null)}
              setMedia={setMedia}
              accountId={accountId}
              onRemoveImage={handleRemoveImage}
              collapsed={collapsed}
            />
          </div>

          {totalItems > 0 && (
            <div className="mt-4 pb-4">
              <Pagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => setCurrentPage(page)}
                onItemsPerPageChange={(count) => {
                  setItemsPerPage(count);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Media;